import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../hooks/useToast';

interface Facility {
  id: string;
  name: string;
  available: boolean;
  price: number;
  notice: string;
  operatingHours: {
    start: number;
    end: number;
  };
  operatingDays: number[];
  closedPeriods?: Array<{
    id: string;
    start: {
      date: string;
      time: number;
    };
    end: {
      date: string;
      time: number;
    };
  }>;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface SearchParams {
  searchTerm: string;
  status?: string;
}

interface FacilityStatistics {
  total: number;
  available: number;
  unavailable: number;
}

const FacilityListPage: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [filteredFacilities, setFilteredFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    searchTerm: '',
    status: ''
  });
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Firestore Timestamp를 Date로 변환하는 헬퍼 함수
  const convertTimestamp = useCallback((timestamp: any): string => {
    if (!timestamp) return new Date().toISOString();
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toISOString();
    }
    if (typeof timestamp === 'string') {
      return timestamp;
    }
    return new Date().toISOString();
  }, []);

  // 시설물 목록 가져오기
  const fetchFacilities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, 'facilities'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const facilitiesData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || '',
          available: data.available ?? true,
          price: data.price || 0,
          notice: data.notice || '',
          operatingHours: data.operatingHours || { start: 9, end: 18 },
          operatingDays: data.operatingDays || [1, 2, 3, 4, 5],
          closedPeriods: data.closedPeriods || [],
          imageUrl: data.imageUrl || '',
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      });

      setFacilities(facilitiesData);
      setFilteredFacilities(facilitiesData);
      showToast('시설물 목록을 성공적으로 불러왔습니다.', { type: 'success' });
    } catch (err) {
      console.error('시설물 목록 조회 실패:', err);
      const errorMessage = err instanceof Error ? err.message : '시설물 목록을 불러오는데 실패했습니다.';
      setError(errorMessage);
      showToast(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [convertTimestamp, showToast]);

  // 권한 체크 및 초기 데이터 로드
  useEffect(() => {
    if (!authLoading) {
      // 최고관리자이거나 시설물 관리 권한이 있는 경우에만 접근 가능
      if (!user || (userLevel !== 1 && !permissions?.facilities)) {
        navigate('/admin/dashboard');
        return;
      }

      fetchFacilities();
    }
  }, [user, userLevel, permissions, authLoading, navigate, fetchFacilities]);

  // 검색 파라미터 업데이트
  const handleSearch = useCallback((params: SearchParams) => {
    setSearchParams(params);
  }, []);

  // 시설물 통계 (메모이제이션)
  const statistics = useMemo((): FacilityStatistics => {
    const total = facilities.length;
    const available = facilities.filter(f => f.available).length;
    const unavailable = total - available;

    return {
      total,
      available,
      unavailable
    };
  }, [facilities]);

  // 필터링된 시설물 목록 (메모이제이션)
  const filteredFacilitiesList = useMemo(() => {
    let filtered = [...facilities];

    // 검색어로 필터링
    if (searchParams.searchTerm.trim()) {
      const searchLower = searchParams.searchTerm.toLowerCase();
      filtered = filtered.filter(facility =>
        facility.name.toLowerCase().includes(searchLower) ||
        facility.notice.toLowerCase().includes(searchLower)
      );
    }

    // 상태로 필터링
    if (searchParams.status) {
      filtered = filtered.filter(facility => {
        if (searchParams.status === 'available') return facility.available;
        if (searchParams.status === 'unavailable') return !facility.available;
        return true;
      });
    }


    return filtered;
  }, [facilities, searchParams]);

  // 필터링된 시설물 상태 업데이트
  useEffect(() => {
    setFilteredFacilities(filteredFacilitiesList);
  }, [filteredFacilitiesList]);

  // 권한 체크 - 인증이 완료된 후에만 확인
  const hasPermission = useMemo(() => {
    if (authLoading) return null;
    if (!user || !userLevel) return false;
    return userLevel === 1 || permissions?.facilities;
  }, [user, userLevel, permissions, authLoading]);

  // 통합된 로딩 상태 - 인증 중일 때
  if (authLoading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb] mx-auto mb-4"></div>
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              권한을 확인하는 중...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 권한이 없는 경우
  if (hasPermission === false) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] mb-2">
                    접근 권한 없음
                  </h2>
                  <p className="text-yellow-700 text-lg font-medium font-['Pretendard'] mb-4">
                    시설물을 관리할 권한이 없습니다.
                  </p>
                  <button
                    onClick={() => navigate('/admin/dashboard')}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-sm hover:bg-gray-50 transition-colors"
                  >
                    대시보드로 돌아가기
                  </button>
                </div>
                <svg className="w-12 h-12 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 1112 0v1H3v-1z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 권한이 확인되지 않은 경우
  if (hasPermission !== true) {
    return null;
  }

  // 로딩 상태 렌더링
  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb] mx-auto mb-4"></div>
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">시설물 데이터를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 렌더링
  if (error) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-bold text-red-800 mb-2">데이터 로드 실패</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchFacilities}
                className="w-[120px] h-10 bg-red-600 text-white text-sm font-bold font-['Pretendard'] rounded-sm hover:bg-red-700 transition-colors"
              >
                다시 시도
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              시설물 관리
            </div>
            <button
              onClick={() => navigate('/admin/facilities/new')}
              className="w-[130px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
            >
              시설물 추가
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>
        
        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard'] mb-2">
                  시설물을 등록하고 관리할 수 있습니다.
                </p>
                <p className="text-[#6b7684] text-sm">
                  이용 가격, 운영 시간, 휴무일 등을 설정하고 예약 상태를 모니터링할 수 있습니다.
                </p>
              </div>
              <button
                onClick={fetchFacilities}
                className="flex items-center gap-2 px-3 py-2 text-[#155acb] hover:bg-[#155acb]/5 rounded transition-colors"
                title="목록 새로고침"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                새로고침
              </button>
            </div>
          </div>
        </div>

        {/* 통계 대시보드 */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-sm outline outline-1 outline-slate-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">전체 시설물</dt>
                  <dd className="text-lg font-medium text-gray-900">{statistics.total}개</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-sm outline outline-1 outline-slate-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">이용가능</dt>
                  <dd className="text-lg font-medium text-gray-900">{statistics.available}개</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-sm outline outline-1 outline-slate-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">점검중</dt>
                  <dd className="text-lg font-medium text-gray-900">{statistics.unavailable}개</dd>
                </dl>
              </div>
            </div>
          </div>

        </div>

        {/* 검색 폼 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              시설물 검색
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">
                  시설물명 검색
                </label>
                <input
                  type="text"
                  value={searchParams.searchTerm}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, searchTerm: e.target.value }))}
                  placeholder="시설물명을 입력해주세요"
                  className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-base font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
                />
              </div>

              <div>
                <label className="block text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">
                  운영 상태
                </label>
                <select
                  value={searchParams.status || ''}
                  onChange={(e) => setSearchParams(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-base font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
                >
                  <option value="">전체</option>
                  <option value="available">이용가능</option>
                  <option value="unavailable">점검중</option>
                </select>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-[#6b7684]">
                <span className="text-blue-600">* 검색 조건을 설정하여 원하는 시설물을 찾아보세요.</span>
              </div>
              <button
                onClick={() => setSearchParams({ searchTerm: '', status: '' })}
                className="px-4 py-2 border border-gray-300 text-[#6b7684] text-sm font-medium font-['Pretendard'] rounded-sm hover:bg-gray-50 transition-colors"
              >
                검색 초기화
              </button>
            </div>
          </div>
        </div>
        
        {/* 시설물 목록 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9">
                시설물 목록
              </h2>
              <div className="text-sm text-[#6b7684]">
                총 {facilities.length}개 / 검색결과 {filteredFacilities.length}개
              </div>
            </div>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

            {filteredFacilities.length === 0 && searchParams.searchTerm ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-lg font-bold text-[#1e2c49] mb-2">검색 결과가 없습니다</h3>
                <p className="text-[#6b7684] mb-4">'{searchParams.searchTerm}'에 대한 검색 결과를 찾을 수 없습니다.</p>
                <button
                  onClick={() => setSearchParams({ searchTerm: '', status: '' })}
                  className="text-[#155acb] hover:text-[#155acb]/80 font-medium"
                >
                  검색 초기화
                </button>
              </div>
            ) : filteredFacilities.length === 0 && !searchParams.searchTerm ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-lg font-bold text-[#1e2c49] mb-2">등록된 시설물이 없습니다</h3>
                <p className="text-[#6b7684] mb-4">아직 시스템에 등록된 시설물이 없습니다.</p>
                <button
                  onClick={() => navigate('/admin/facilities/new')}
                  className="w-[130px] h-10 bg-[#155acb] text-white text-sm font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                >
                  첫 시설물 추가
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-sm outline outline-1 outline-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#e5e8eb]">
                    <thead className="bg-[#f8fafc]">
                      <tr>
                        <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                          시설물명
                        </th>
                        <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                          이용가격
                        </th>
                        <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                          운영시간
                        </th>
                        <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                          상태
                        </th>
                        <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                          등록일시
                        </th>
                        <th className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#e5e8eb]">
                      {filteredFacilities.map((facility) => (
                        <tr
                          key={facility.id}
                          className="hover:bg-[#f8fafc] transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {facility.imageUrl && (
                                <div className="flex-shrink-0 h-10 w-10 mr-4">
                                  <img
                                    className="h-10 w-10 rounded-full object-cover"
                                    src={facility.imageUrl}
                                    alt={facility.name}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              <div>
                                <div className="text-base font-semibold font-['Pretendard'] text-[#1e2c49]">
                                  {facility.name}
                                </div>
                                {facility.notice && (
                                  <div className="text-sm text-[#6b7684] mt-1 truncate max-w-xs">
                                    {facility.notice}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-base font-['Pretendard'] text-[#6b7684]">
                              {facility.price === 0 ? '무료' : `${facility.price.toLocaleString()}원`}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-[#6b7684]">
                              {facility.operatingHours.start}:00 - {facility.operatingHours.end}:00
                            </div>
                            <div className="text-xs text-[#9ca3af] mt-1">
                              {facility.operatingDays.length === 7
                                ? '매일'
                                : `주 ${facility.operatingDays.length}일`
                              }
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium font-['Pretendard'] ${
                              facility.available
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {facility.available ? '이용가능' : '점검중'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-base font-['Pretendard'] text-[#6b7684]">
                              {new Date(facility.createdAt).toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => navigate(`/admin/facilities/${facility.id}`)}
                                className="text-[#155acb] hover:text-[#155acb]/80 font-medium"
                              >
                                상세보기
                              </button>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={() => navigate(`/admin/facilities/${facility.id}?edit=true`)}
                                className="text-green-600 hover:text-green-500 font-medium"
                              >
                                수정
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default FacilityListPage; 